import { useState } from 'react';

import { ContentCopy as ContentCopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { Box, Divider, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { ParsedWaiverRecord } from '@/apps/engagement-letter-database/store/useWaiverRecord';

dayjs.extend(utc);

interface WaiverInfoPaneProps {
  record: ParsedWaiverRecord;
}

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value || 'N/A'}</Typography>
  </Box>
);

const WaiverInfoPane = ({ record }: WaiverInfoPaneProps) => {
  const [copied, setCopied] = useState(false);

  // Waiver letter dates are `timestamptz` stored at UTC midnight (calendar
  // dates). Format from the UTC date so they are not shifted a day behind by
  // the viewer's local timezone.
  const adverseLetterDate = record.adverseLetterDate ? dayjs.utc(record.adverseLetterDate).format('M/D/YYYY') : 'N/A';
  const clientLetterDate = record.clientLetterDate ? dayjs.utc(record.clientLetterDate).format('M/D/YYYY') : 'N/A';

  const handleCopy = async () => {
    const columns: { label: string; value: string }[] = [
      { label: 'Client Number', value: record.clientNumber || 'N/A' },
      { label: 'Matter Number', value: record.matterNumber || 'N/A' },
      { label: 'Client Contact', value: record.clientContact || 'N/A' },
      { label: 'Client Letter Date', value: clientLetterDate },
      { label: 'Client MLB Attorney', value: record.clientAttorney || 'N/A' },
      { label: 'Adverse Client Name', value: record.adverseClientName || 'N/A' },
      { label: 'Adverse Contact Signer', value: record.adverseContactSigner || 'N/A' },
      { label: 'Adverse Letter Date', value: adverseLetterDate },
      { label: 'Adverse MLB Attorney', value: record.adverseAttorney || 'N/A' },
      { label: 'Multiple Waivers', value: record.multipleWaivers || 'N/A' },
      ...(record.comments ? [{ label: 'Comments', value: record.comments }] : []),
    ];

    const escapeHtml = (value: string) =>
      value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

    const headerCells = columns
      .map(
        (col) =>
          `<th style="border:1px solid #999;padding:6px 10px;background-color:#f2f2f2;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.label,
          )}</th>`,
      )
      .join('');
    const valueCells = columns
      .map(
        (col) =>
          `<td style="border:1px solid #999;padding:6px 10px;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.value,
          )}</td>`,
      )
      .join('');
    const html = `<table style="border-collapse:collapse;"><thead><tr>${headerCells}</tr></thead><tbody><tr>${valueCells}</tr></tbody></table>`;

    const plainText = `${columns.map((col) => col.label).join('\t')}\n${columns.map((col) => col.value).join('\t')}`;

    try {
      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy waiver information:', error);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>
          Waiver Details
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy as table'}>
          <IconButton onClick={() => void handleCopy()} size="small" aria-label="Copy waiver information as table">
            {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Client Number" value={record.clientNumber} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Matter Number" value={record.matterNumber} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Client Contact" value={record.clientContact} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Client Letter Date" value={clientLetterDate} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Client MLB Attorney" value={record.clientAttorney} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Adverse Client Name" value={record.adverseClientName} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Adverse Contact Signer" value={record.adverseContactSigner} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Adverse Letter Date" value={adverseLetterDate} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Adverse MLB Attorney" value={record.adverseAttorney} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoField label="Multiple Waivers" value={record.multipleWaivers} />
        </Grid>
        {record.comments && (
          <Grid size={{ xs: 12 }}>
            <InfoField label="Comments" value={record.comments} />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default WaiverInfoPane;
